import React, { createContext, useContext, useEffect, useReducer, useRef } from "react";
import SharedRoomsState from "./room/room-state";
import SharedStatusState from "./status/status-state";
import SharedRoomsActions from "./room/room-actions";
import SharedStatusActions from "./status/status-actions";
import SharedSchedulerActions from "./room/scheduler/scheduler-actions";
import SharedZonePlannerActions from "./room/zone-planner/zone-planner-actions";
import SharedRobotConfigurationsActions from "./robot-configurations/robot-configurations-actions";
import SharedAppConfigurationsState from "./app-configurations/app-configurations-state";
import SharedRobotConfigurationsState from "./robot-configurations/robot-configurations-state";
import { shallowEqual } from "react-redux";
import SharedMapsActions from "./room/maps/maps-actions";

export interface SharedState {
  appConfigurations: SharedAppConfigurationsState;
  rooms: SharedRoomsState;
  status: SharedStatusState;
  robotConfigurations: SharedRobotConfigurationsState;
}

export interface SharedActions
  extends
    SharedRoomsActions,
    SharedStatusActions,
    SharedZonePlannerActions,
    SharedSchedulerActions,
    SharedMapsActions,
    SharedRobotConfigurationsActions {}

export interface SharedContextValue {
  getState: () => SharedState;
  actions: SharedActions;
  dispatch: (action: ReturnType<SharedActions[keyof SharedActions]>) => void;
  subscribe: (listener: () => void) => () => void;
}

const SharedContext = createContext<SharedContextValue | undefined>(undefined);

export const SharedProvider: React.FC<{
  store: SharedContextValue;
  children: React.ReactNode;
}> = ({ store, children }) => {
  return <SharedContext.Provider value={store}>{children}</SharedContext.Provider>;
};

export const useSharedSelector = <T,>(
  selector: (state: SharedState) => T,
  equalityFn: (a: T, b: T) => boolean = shallowEqual
): T => {
  const ctx = useContext(SharedContext);
  if (!ctx) throw new Error("useSharedSelector must be used inside SharedProvider");

  const [, forceRender] = useReducer((s) => s + 1, 0);

  const currentSelectionRef = useRef<T>(selector(ctx.getState()));
  // const currentSelection = selector(ctx.getState());

  useEffect(() => {
    const checkForUpdates = () => {
      const newSelection = selector(ctx.getState());
      if (!equalityFn(newSelection, currentSelectionRef.current)) {
        currentSelectionRef.current = newSelection;
        forceRender();
      }
    };

    const unsubscribe = ctx.subscribe(checkForUpdates);
    return unsubscribe;
  }, [ctx]);

  return currentSelectionRef.current;
};

export const useSharedActions = () => {
  const ctx = useContext(SharedContext);
  if (!ctx) throw new Error("useSharedAction must be used inside SharedProvider");
  return ctx.actions;
};

export const useSharedDispatch = () => {
  const ctx = useContext(SharedContext);
  if (!ctx) throw new Error("useSharedDispatch must be used inside SharedProvider");
  return ctx.dispatch;
};

export default SharedProvider;
